import React from 'react'
import styled, { css } from 'styled-components'
import { VisuallyHidden } from '@mote/ui'
import { useMotePlayer } from '../../../shared/contexts/motePlayer.context'
import {
  Scrubber,
  Durations,
  MotePlayerProviders
} from '../../../shared/components'
import { Transcription } from '../Transcription'
import { Options } from '../Options'
import { Controls } from '../Controls'

const CompactCardRoot = ({
  audioUrl,
  moteId,
  totalTime,
  scrubberPercentage,
  disabled,
  loading,
  onPlay,
  onPause,
  onEnd,
  useInEditor,
  dynamicWidth,
  transcriptionVisible,
  transcription,
  isQuiescent,
  editor,
  isPreparing,
  isStandalone = true
}) => {
  const [currentTime, setCurrentTime] = React.useState(0)
  const [forcePause, setForcePause] = React.useState(false)
  const [showEditor, setShowEditor] = React.useState(false)

  const options = React.useMemo(() => {
    return {
      init: isStandalone,
      src: audioUrl,
      initialPercentagePosition: scrubberPercentage,
      isStandalone,
      playbackCallbacks: {
        onPlay,
        onPause,
        onEnd
      }
    }
  }, [audioUrl, scrubberPercentage, isStandalone, onPlay, onPause, onEnd])

  const { playerProps } = useMotePlayer(options)

  React.useEffect(() => {
    const currTime = +totalTime * (+scrubberPercentage / 100)
    setCurrentTime(currTime)
    if (+scrubberPercentage) setForcePause(true)
  }, [scrubberPercentage, totalTime])

  return (
    <PlayerAdjustment
      useInEditor={useInEditor}
      data-mote-id={moteId}
      onClick={e => e.stopPropagation()}
      dynamicWidth={dynamicWidth}
      {...playerProps}
    >
      <VisuallyHidden as='h2'>{playerProps['aria-label']}</VisuallyHidden>
      {showEditor &&
        editor &&
        editor({
          onClose: () => setShowEditor(false)
        })}
      {editor && !isPreparing && !loading && (
        <StyledOptions moteId={moteId} onEdit={() => setShowEditor(true)} />
      )}
      <Controls disabled={disabled} loading={loading} />

      <ScrubberAdjustment
        useInEditor={useInEditor}
        isTranscriptionVisible={transcriptionVisible && !loading}
      >
        {useInEditor && (
          <Durations currentTime={currentTime} totalTime={totalTime} />
        )}
        <Scrubber
          variant='md'
          loading={loading}
          currentTime={currentTime}
          totalTime={totalTime}
        />
      </ScrubberAdjustment>
      {transcriptionVisible && !loading && (
        <Transcription
          moteId={moteId}
          text={transcription}
          isQuiescent={isQuiescent}
          isLandingPage={false}
          isLoading={loading}
          size='sm'
        />
      )}
    </PlayerAdjustment>
  )
}

export const CompactCard = ({ autoFocus, ...rest }) => (
  <MotePlayerProviders autoFocus={autoFocus} isStandalone={rest.isStandalone}>
    <CompactCardRoot {...rest} />
  </MotePlayerProviders>
)

const PlayerAdjustment = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  outline: transparent solid 2px !important;
  outline-offset: 2px;

  * {
    font-family: 'DM Sans', sans-serif !important;
    box-sizing: border-box;
  }

  ${({ useInEditor, dynamicWidth }) =>
    useInEditor
      ? css`
          margin-top: 16px;
          margin-bottom: 24px;
        `
      : css`
          padding: 8px;
          border: 1px solid #ececf3;
          border-radius: 8px;
          width: ${dynamicWidth ? 'auto' : '260px'};
        `};
`

const ScrubberAdjustment = styled.div`
  ${({ useInEditor, isTranscriptionVisible }) =>
    useInEditor
      ? css`
          margin: 0 48px;
        `
      : css`
          margin: ${isTranscriptionVisible ? 8 : 0}px 40px 0 40px;
        `};
`

const StyledOptions = styled(Options)`
  margin-left: calc(100% - 22px);
`
